import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import {
  ArrowLeft,
  Printer,
  Download,
  Save,
  LineChart as LineChartIcon,
  Lock,
} from "lucide-react";
import { BrandMark, BrandWordmark } from "../../pages/LandingPage";
import ShareScoreDialog from "../ShareScoreDialog";
import { getProToken, buildPdfDownloadUrl } from "../../lib/pro";

/**
 * Sticky top action bar for the report page. Carries:
 *   - Brand mark + name (links home)
 *   - "New analysis" back link
 *   - "Trends ({n})" link when history exists
 *   - "Save & compare" button
 *   - "Share score" dialog launcher
 *   - "Download PDF" / "Unlock PDF" entitlement-aware button
 *
 * The Download PDF button prefers the server-side endpoint when we have
 * a report.id + paid unlock_token (gets a real .pdf file via the browser's
 * native download flow). When neither is available (sample mode, no token),
 * it falls back to window.print() which opens the system print dialog with
 * "Save as PDF" pre-selected as the printer destination on most platforms.
 */
export default function Header({
  report,
  onSave,
  savedThis,
  historyCount,
  onPrint,
  pdfActive,
  proActive,
}) {
  const unlocked = pdfActive || proActive;
  const reportId = report?.id;
  const token = unlocked ? getProToken() : null;
  const directPdfUrl =
    reportId && token ? buildPdfDownloadUrl(reportId, token) : null;

  return (
    <header className="max-w-7xl mx-auto px-6 sm:px-10 pt-8 flex flex-wrap items-center justify-between gap-3 no-print">
      <Link to="/" className="flex items-center gap-2.5">
        <BrandWordmark size="md" />
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/analyze">
          <Button variant="ghost" className="text-[#5C665D] hover:bg-[#D4C9B6]/40" data-testid="back-to-analyze-btn">
            <ArrowLeft size={14} strokeWidth={1.5} className="mr-2" />
            New analysis
          </Button>
        </Link>
        {historyCount > 0 && (
          <Link to="/trends">
            <Button variant="ghost" className="text-[#5C665D] hover:bg-[#D4C9B6]/40" data-testid="view-trends-btn">
              <LineChartIcon size={14} strokeWidth={1.5} className="mr-2" />
              Trends ({historyCount})
            </Button>
          </Link>
        )}
        <Button
          variant="outline"
          onClick={onSave}
          disabled={savedThis}
          className="border-[#1F382E] text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] transition-all duration-300"
          data-testid="save-period-btn"
        >
          <Save size={14} strokeWidth={1.5} className="mr-2" />
          {savedThis ? "Saved" : "Save & compare"}
        </Button>
        <ShareScoreDialog report={report} />
        {/* Direct-download path — preferred when we have report_id + token. */}
        {directPdfUrl && (
          <Button
            asChild
            variant="outline"
            className="border-[#1F382E] text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] transition-all duration-300"
            data-testid="download-pdf-btn"
            title="Download the official PDF report."
          >
            <a href={directPdfUrl} download>
              <Download size={14} strokeWidth={1.5} className="mr-2" />
              Download PDF
            </a>
          </Button>
        )}
        {/* Print-dialog fallback — for unlocked-but-tokenless (e.g. sample mode)
            OR locked state (CTA to unlock). */}
        {!directPdfUrl && (
          <Button
            onClick={onPrint}
            variant="outline"
            className={`transition-all duration-300 ${
              unlocked
                ? "border-[#1F382E] text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0]"
                : "border-[#C26D5C] text-[#C26D5C] hover:bg-[#C26D5C] hover:text-white"
            }`}
            data-testid="print-report-btn"
            title={
              unlocked
                ? "Opens the print dialog. Choose 'Save as PDF' as your printer to download the PDF."
                : "Locked — unlock to download the PDF."
            }
          >
            {unlocked ? (
              <Printer size={14} strokeWidth={1.5} className="mr-2" />
            ) : (
              <Lock size={14} strokeWidth={1.5} className="mr-2" />
            )}
            {unlocked ? "Print / Save as PDF" : "Unlock PDF"}
          </Button>
        )}
      </div>
    </header>
  );
}
